import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/contexts/AuthContext";
import { MeasureManagement } from "@/components/admin/MeasureManagement";
import { ProgramYearSettings } from "@/components/configuration/ProgramYearSettings";
import { YearLockingSettings } from "@/components/admin/YearLockingSettings";
import { PaymentAdjustmentConfig } from "@/components/admin/PaymentAdjustmentConfig";
import { ChartAbstractionQuestionsConfig } from "@/components/admin/ChartAbstractionQuestionsConfig";
import { SetPasswordDialog } from "@/components/admin/SetPasswordDialog";
import { ChangePasswordDialog } from "@/components/admin/ChangePasswordDialog";
import { Key, RefreshCw, Trash2 } from "lucide-react";
import { useToast } from "@/components/ui/use-toast";
import { apiClient } from "@/lib/api-client";
import { adminUsersCache, fetchAdminUsers } from "@/lib/admin-cache";
import { PLATFORM_MODULES } from "@/platform/modules";

export default function Admin() {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const { toast } = useToast();
  const [practiceList, setPracticeList] = useState<any[]>([]);
  const [selected, setSelected] = useState<number | null>(null);
  // Instant render from the shared cache (prefetched at login by
  // DashboardLayout; see src/lib/admin-cache.ts) — a background
  // refresh still runs and updates rows in place.
  const [users, setUsers] = useState<any[]>(adminUsersCache.users);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [selectedUser, setSelectedUser] = useState<any>(null);
  const [showSetPasswordDialog, setShowSetPasswordDialog] = useState(false);
  const [showChangePasswordDialog, setShowChangePasswordDialog] = useState(false);
  // P4: per-user program grants (Programs column)
  const [userPrograms, setUserPrograms] = useState<Record<number, string[]>>(adminUsersCache.programs);
  // Controlled tab + kept-mounted panels (see below). Default Users.
  const [adminTab, setAdminTab] = useState('users');
  // Inner scrollport for tab panels — keeps document height stable (see return).
  const panelScrollRef = useRef<HTMLDivElement>(null);
  const current = typeof window !== "undefined" ? localStorage.getItem("currentPractice") : null;

  const handleAdminTabChange = (value: string) => {
    setAdminTab(value);
    panelScrollRef.current?.scrollTo(0, 0);
  };

  useEffect(() => {
    try {
      const stored = JSON.parse(localStorage.getItem("practices") || "null") || [];
      const single = JSON.parse(localStorage.getItem("practiceInfo") || "null");
      const arr = Array.isArray(stored) && stored.length ? stored : single ? [single] : [];
      setPracticeList(arr);
    } catch {}
  }, []);

  // Fetch users when component mounts
  useEffect(() => {
    if (user?.role === 'admin' && token) {
      fetchUsers();
    }
  }, [user, token]);

  // P4: toggle a user's APP Plus grant. MIPS is deliberately NOT togglable
  // (security review S2): MIPS routes aren't program-wrapped yet, so revoking
  // the grant would remove the tile while leaving every MIPS API accessible —
  // a false control. Deactivating the user is the real kill switch.
  const handleToggleAppPlus = async (userItem: any) => {
    const current = userPrograms[userItem.id] ?? ['mips'];
    const next = current.includes('app-plus')
      ? current.filter(p => p !== 'app-plus')
      : [...current, 'app-plus'];
    try {
      const res = await apiClient.put(`/api/programs/users/${userItem.id}`, { programs: next });
      adminUsersCache.programs = { ...adminUsersCache.programs, [userItem.id]: res?.programs ?? next };
      setUserPrograms(prev => ({ ...prev, [userItem.id]: res?.programs ?? next }));
      toast({
        title: "Program access updated",
        description: `${userItem.name}: ${(res?.programs ?? next).join(', ')}`,
      });
    } catch (error) {
      console.error('Error updating program access:', error);
      toast({ title: "Error", description: "Failed to update program access", variant: "destructive" });
    }
  };

  const fetchUsers = async () => {
    // Spinner only when we have nothing to show — with cached rows the table
    // stays visible and the refresh happens silently in the background.
    if (adminUsersCache.users.length === 0) setLoadingUsers(true);
    try {
      await fetchAdminUsers(token!);
      setUsers(adminUsersCache.users);
      setUserPrograms(adminUsersCache.programs);
    } catch (error) {
      console.error('Error fetching users:', error);
      toast({
        title: "Error",
        description: "Failed to fetch users",
        variant: "destructive",
      });
    } finally {
      setLoadingUsers(false);
    }
  };

  const handleSelect = (index: number) => {
    setSelected(index);
    const pick = practiceList[index];
    localStorage.setItem("currentPractice", pick.name || `Practice ${index + 1}`);
    navigate("/dashboard");
  };

  const handleSetPassword = (userToReset: any) => {
    setSelectedUser(userToReset);
    setShowSetPasswordDialog(true);
  };

  const handleChangeOwnPassword = () => {
    setShowChangePasswordDialog(true);
  };

  const handleToggleActive = async (userItem: any) => {
    const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001';
    const nextActive = !userItem.is_active;
    try {
      const response = await fetch(`${apiUrl}/api/users/${userItem.id}/active`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ is_active: nextActive }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to update user');
      toast({
        title: nextActive ? 'User reactivated' : 'User deactivated',
        description: `${userItem.email} can ${nextActive ? 'now sign in' : 'no longer sign in'}.`,
      });
      fetchUsers();
    } catch (err) {
      toast({ title: 'Error', description: err instanceof Error ? err.message : 'Failed to update user', variant: 'destructive' });
    }
  };

  const handleDeleteUser = async (userItem: any) => {
    if (!window.confirm(`Permanently delete ${userItem.email}? This cannot be undone.`)) return;
    const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001';
    try {
      const response = await fetch(`${apiUrl}/api/users/${userItem.id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` },
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error || 'Failed to delete user');
      toast({ title: 'User deleted', description: `${userItem.email} has been removed.` });
      fetchUsers();
    } catch (err) {
      toast({ title: 'Could not delete user', description: err instanceof Error ? err.message : 'Failed to delete user', variant: 'destructive' });
    }
  };

  const onPasswordDialogClose = () => {
    setShowSetPasswordDialog(false);
    setShowChangePasswordDialog(false);
    setSelectedUser(null);
    // Optionally refresh users list
    if (user?.role === 'admin' && token) {
      fetchUsers();
    }
  };

  // Fixed-height shell: sticky header (h-14) + main padding (p-6 = 3rem).
  // Tab panels scroll INSIDE this shell so switching tabs never changes
  // document height — that reflow (scrollbar / sticky header) was the jump.
  return (
    <div className="flex h-[calc(100svh-6.5rem)] flex-col">
      <Helmet>
        <title>Admin | VBCA</title>
        <meta name="description" content="Registry staff tools: manage clients, monitor statuses, view logs, and impersonate for support." />
        <link rel="canonical" href="/admin" />
      </Helmet>

      <h1 className="mb-6 shrink-0 text-3xl font-bold">Administration</h1>

      <Tabs
        value={adminTab}
        onValueChange={handleAdminTabChange}
        className="flex min-h-0 flex-1 flex-col"
      >
        <TabsList className="grid w-full shrink-0 grid-cols-5">
          <TabsTrigger value="users">User Management</TabsTrigger>
          <TabsTrigger value="practices">Practices</TabsTrigger>
          <TabsTrigger value="measures">Measure Logic</TabsTrigger>
          <TabsTrigger value="chart-abstraction">Chart Abstraction</TabsTrigger>
          <TabsTrigger value="system">System Settings</TabsTrigger>
        </TabsList>

        <div
          ref={panelScrollRef}
          className="mt-6 min-h-0 flex-1 overflow-y-auto overscroll-contain"
        >
        <div
          className={adminTab === 'users' ? undefined : 'hidden'}
          role="tabpanel"
          aria-hidden={adminTab !== 'users'}
        >
          {user?.role === 'admin' ? (
            <div className="space-y-6">
              {/* Create User Section */}
              <Card>
                <CardHeader>
                  <CardTitle>Create New User</CardTitle>
                  <CardDescription>Add new users to the system.</CardDescription>
                </CardHeader>
                <CardContent>
                  <CreateUserForm token={token} onUserCreated={fetchUsers} />
                </CardContent>
              </Card>

              {/* User List Section */}
              <Card>
                <CardHeader>
                  <CardTitle>Existing Users</CardTitle>
                  <CardDescription>Manage user accounts and passwords.</CardDescription>
                </CardHeader>
                <CardContent>
                  {loadingUsers ? (
                    <div className="flex justify-center py-8">
                      <RefreshCw className="h-6 w-6 animate-spin text-muted-foreground" />
                    </div>
                  ) : users.length === 0 ? (
                    <p className="text-muted-foreground text-center py-8">No users found.</p>
                  ) : (
                    <div className="rounded-lg border">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Name</TableHead>
                            <TableHead>Email</TableHead>
                            <TableHead>Role</TableHead>
                            <TableHead>Status</TableHead>
                            <TableHead>Programs</TableHead>
                            <TableHead>Created</TableHead>
                            <TableHead className="text-right">Actions</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {users.map((userItem) => (
                            <TableRow key={userItem.id}>
                              <TableCell className="font-medium">{userItem.name}</TableCell>
                              <TableCell>{userItem.email}</TableCell>
                              <TableCell>
                                <Badge variant={userItem.role === 'admin' ? 'default' : 'secondary'}>
                                  {userItem.role}
                                </Badge>
                              </TableCell>
                              <TableCell>
                                <Badge variant={userItem.is_active === false ? 'outline' : 'secondary'}>
                                  {userItem.is_active === false ? 'Inactive' : 'Active'}
                                </Badge>
                              </TableCell>
                              <TableCell>
                                {userItem.id === 0 ? (
                                  <span className="text-xs text-muted-foreground">—</span>
                                ) : (
                                  <div className="flex items-center gap-1.5">
                                    {/* MIPS: enforced by client/practice assignment; per-program
                                        revocation activates when MIPS routes are wrapped (S2) */}
                                    <Badge
                                      variant="secondary"
                                      title="MIPS access is enforced by client/practice assignment; per-program MIPS revocation activates when MIPS routes are wrapped in a later phase."
                                    >
                                      MIPS
                                    </Badge>
                                    {(userPrograms[userItem.id] ?? []).includes('app-plus') ? (
                                      <Badge
                                        className="cursor-pointer"
                                        title="Click to revoke APP Plus access"
                                        onClick={() => handleToggleAppPlus(userItem)}
                                      >
                                        APP+
                                      </Badge>
                                    ) : (
                                      <Badge
                                        variant="outline"
                                        className="cursor-pointer text-muted-foreground"
                                        title="Click to grant APP Plus access"
                                        onClick={() => handleToggleAppPlus(userItem)}
                                      >
                                        + APP+
                                      </Badge>
                                    )}
                                  </div>
                                )}
                              </TableCell>
                              <TableCell>
                                {new Date(userItem.created_at).toLocaleDateString()}
                              </TableCell>
                              <TableCell className="text-right">
                                {userItem.id == user?.id ? (
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={handleChangeOwnPassword}
                                  >
                                    <Key className="h-4 w-4 mr-2" />
                                    Change My Password
                                  </Button>
                                ) : userItem.id === 0 ? (
                                  <span className="text-xs text-muted-foreground">System account</span>
                                ) : (
                                  <div className="flex items-center justify-end gap-2">
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      onClick={() => handleSetPassword(userItem)}
                                    >
                                      <Key className="h-4 w-4 mr-2" />
                                      Set Password
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      onClick={() => handleToggleActive(userItem)}
                                    >
                                      {userItem.is_active === false ? 'Reactivate' : 'Deactivate'}
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="destructive"
                                      onClick={() => handleDeleteUser(userItem)}
                                    >
                                      <Trash2 className="h-4 w-4" />
                                    </Button>
                                  </div>
                                )}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          ) : (
            <Card>
              <CardContent className="pt-6">
                <p className="text-muted-foreground">You do not have permission to manage users.</p>
              </CardContent>
            </Card>
          )}
        </div>

        <div
          className={adminTab === 'practices' ? undefined : 'hidden'}
          role="tabpanel"
          aria-hidden={adminTab !== 'practices'}
        >
          <Card>
            <CardHeader>
              <CardTitle>Practice Management</CardTitle>
              <CardDescription>View and manage client practices.</CardDescription>
            </CardHeader>
            <CardContent>
              {practiceList.length === 0 ? (
                <p>No practice data found. Please complete onboarding.</p>
              ) : (
                <div className="rounded-lg border">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Practice Name</TableHead>
                        <TableHead>TIN</TableHead>
                        <TableHead>Status</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {practiceList.map((practice, index) => (
                        <TableRow key={practice.tin || practice.name || index}>
                          <TableCell>{practice.name || `Practice ${index + 1}`}</TableCell>
                          <TableCell>{practice.tin || '—'}</TableCell>
                          <TableCell>
                            <Badge variant={current === practice.name ? 'default' : 'secondary'}>
                              {current === practice.name ? 'Current' : 'Available'}
                            </Badge>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        <div
          className={adminTab === 'measures' ? undefined : 'hidden'}
          role="tabpanel"
          aria-hidden={adminTab !== 'measures'}
        >
          {user?.role === 'admin' ? (
            <MeasureManagement />
          ) : (
            <Card>
              <CardContent className="pt-6">
                <p className="text-muted-foreground">You do not have permission to manage measures.</p>
              </CardContent>
            </Card>
          )}
        </div>

        <div
          className={adminTab === 'chart-abstraction' ? undefined : 'hidden'}
          role="tabpanel"
          aria-hidden={adminTab !== 'chart-abstraction'}
        >
          {user?.role === 'admin' ? (
            <ChartAbstractionQuestionsConfig />
          ) : (
            <Card>
              <CardContent className="pt-6">
                <p className="text-muted-foreground">You do not have permission to manage chart abstraction questions.</p>
              </CardContent>
            </Card>
          )}
        </div>

        <div
          className={adminTab === 'system' ? undefined : 'hidden'}
          role="tabpanel"
          aria-hidden={adminTab !== 'system'}
        >
          {user?.role === 'admin' ? (
            <div className="space-y-6">
              <ProgramYearSettings />
              <PaymentAdjustmentConfig />
              <YearLockingSettings />
            </div>
          ) : (
            <Card>
              <CardContent className="pt-6">
                <p className="text-muted-foreground">You do not have permission to manage system settings.</p>
              </CardContent>
            </Card>
          )}
        </div>
        </div>
      </Tabs>

      {/* Password Management Dialogs */}
      {selectedUser && (
        <SetPasswordDialog
          open={showSetPasswordDialog}
          onOpenChange={onPasswordDialogClose}
          user={selectedUser}
          token={token || ''}
        />
      )}

      <ChangePasswordDialog
        open={showChangePasswordDialog}
        onOpenChange={onPasswordDialogClose}
        token={token || ''}
      />
    </div>
  );
}

// CreateUserForm Component
interface CreateUserFormProps {
  token: string | null;
  onUserCreated?: () => void;
}

function CreateUserForm({ token, onUserCreated }: CreateUserFormProps) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  
  const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      const response = await fetch(`${apiUrl}/api/users/create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ name, email, password }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to create user');
      }

      setSuccess(`Successfully created user: ${data.name} (${data.email})`);
      setName('');
      setEmail('');
      setPassword('');
      
      // Call the callback to refresh user list
      if (onUserCreated) {
        onUserCreated();
      }

    } catch (err) {
      setError(err instanceof Error ? err.message : 'An unknown error occurred');
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          {error}
        </div>
      )}
      {success && (
        <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg">
          {success}
        </div>
      )}
      
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="space-y-2">
          <label htmlFor="name" className="block text-sm font-medium text-foreground">
            Full Name
          </label>
          <input
            type="text"
            id="name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            placeholder="Enter full name"
            className="w-full px-4 py-3 rounded-lg border border-input bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent transition-colors"
          />
        </div>
        <div className="space-y-2">
          <label htmlFor="email" className="block text-sm font-medium text-foreground">
            Email Address
          </label>
          <input
            type="email"
            id="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            placeholder="Enter email address"
            className="w-full px-4 py-3 rounded-lg border border-input bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent transition-colors"
          />
        </div>
        <div className="space-y-2">
          <label htmlFor="password" className="block text-sm font-medium text-foreground">
            Password
          </label>
          <input
            type="password"
            id="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            placeholder="Enter password"
            className="w-full px-4 py-3 rounded-lg border border-input bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent transition-colors"
          />
        </div>
      </div>
      
      <div className="flex justify-start">
        <Button type="submit" disabled={loading} className="px-6 py-2">
          {loading ? 'Creating...' : 'Create User'}
        </Button>
      </div>
    </form>
  );
}
